﻿import { get, post, type Me, type UploadSessionState } from "./api.ts";
import { hashBlob, partRange, revisionManifestHash } from "./transfer.ts";
import {
  readCheckpoint,
  saveCheckpoint,
  uploadJournalKey,
  type UploadCheckpoint,
} from "./upload-journal.ts";

export type UploadProgress = {
  phase: "hashing" | "preparing" | "uploading" | "completing" | "verifying" | "publishing" | "done";
  fraction: number;
};
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

async function putPart(url: string, body: Blob, attempts = 4): Promise<void> {
  for (let i = 1; ; i++) {
    try {
      const res = await fetch(url, { method: "PUT", body });
      if (res.ok) return;
      throw new Error(`Part upload failed (${res.status}). Reselect the file to resume.`);
    } catch (e) {
      if (i >= attempts) throw e;
      await sleep(500 * 2 ** i);
    }
  }
}

type UploadArgs = {
  tripId: string;
  deliverableId: string;
  nodeId: string;
  file: File;
  notes: string;
  /** A server-side draft left unfinished earlier: continue it instead of starting a second one. */
  resumeDraftId?: string;
  onProgress: (p: UploadProgress) => void;
};

/** Reselecting the same bytes resumes the account-scoped checkpoint after a reload or lost response. */
export async function uploadRevision(args: UploadArgs): Promise<{ versionNumber: number }> {
  const { tripId, deliverableId, nodeId, file, onProgress } = args;
  if (file.size === 0) throw new Error("Choose a video or image file that is not empty.");
  onProgress({ phase: "hashing", fraction: 0 });
  const sha = await hashBlob(file, (fraction) => onProgress({ phase: "hashing", fraction }));
  const me = await get<Me>("/v1/me");
  const key = uploadJournalKey(me.user.id, tripId, deliverableId, nodeId, sha);
  // Serialize the same upload across tabs where Web Locks is available.
  if (globalThis.navigator?.locks) {
    return navigator.locks.request(key, () => resumeRevision(args, key, sha));
  }
  return resumeRevision(args, key, sha);
}

async function resumeRevision(
  args: UploadArgs,
  key: string,
  sha: string,
): Promise<{ versionNumber: number }> {
  const { tripId, deliverableId, nodeId, file, onProgress } = args;
  const base = `/v1/trips/${tripId}`;
  const sizeBytes = String(file.size);
  const checkpoint: UploadCheckpoint = readCheckpoint(key, sizeBytes, sha) ?? {
    version: 1,
    submissionId: crypto.randomUUID(),
    draftKey: crypto.randomUUID(),
    sessionKey: crypto.randomUUID(),
    completeKey: crypto.randomUUID(),
    publishKey: crypto.randomUUID(),
    notes: args.notes,
    mediaType: file.type || "application/octet-stream",
    fingerprint: {
      sourceIdentity: file.name,
      sizeBytes,
      lastModified: new Date(file.lastModified).toISOString(),
      providerGeneration: null,
      sha256: sha,
    },
  };
  // A draft already on the server is continued rather than duplicated; a draft this browser's own
  // journal already knows about wins, since its session and keys belong to that one.
  if (!checkpoint.draftId && args.resumeDraftId) checkpoint.draftId = args.resumeDraftId;
  saveCheckpoint(key, checkpoint);
  onProgress({ phase: "preparing", fraction: 0 });
  if (!checkpoint.draftId) {
    const draft = await post<{ revisionDraftId: string }>(
      `${base}/deliverables/${deliverableId}/revision-drafts`,
      {
        clientSubmissionId: checkpoint.submissionId,
        sizeBytes,
        sha256: sha,
        mediaType: checkpoint.mediaType,
      },
      checkpoint.draftKey,
    );
    checkpoint.draftId = draft.revisionDraftId;
    saveCheckpoint(key, checkpoint);
  }
  if (!checkpoint.sessionId) {
    const session = await post<{ id: string }>(
      `${base}/upload-sessions`,
      {
        origin: { kind: "REVISION_DRAFT", revisionDraftId: checkpoint.draftId },
        sourceFingerprint: checkpoint.fingerprint,
        targetNodeId: nodeId,
      },
      checkpoint.sessionKey,
    );
    checkpoint.sessionId = session.id;
    saveCheckpoint(key, checkpoint);
  }
  const sessionPath = `${base}/upload-sessions/${checkpoint.sessionId}`;
  let state = await get<UploadSessionState>(sessionPath);
  const terminal = (s: UploadSessionState) => {
    if (s.state === "FAILED" || s.state === "CANCELLED") {
      localStorage.removeItem(key);
      throw new Error("This upload failed or expired. Reselect the file to start a new upload.");
    }
  };
  terminal(state);
  while (state.nextAction === "REQUEST_PART_GRANTS") {
    // missingParts is paged; only provider-observed, correctly sized parts count as uploaded.
    const confirmed = new Set(
      state.observedParts
        .filter((p) => {
          const [start, end] = partRange(file.size, p.partNumber);
          return (
            p.partNumber >= 1 && p.partNumber <= state.partCount && p.sizeBytes === end - start
          );
        })
        .map((p) => p.partNumber),
    );
    onProgress({ phase: "uploading", fraction: confirmed.size / state.partCount });
    if (state.missingParts.length === 0)
      throw new Error("Upload state is incomplete. Try resuming again.");
    for (let i = 0; i < state.missingParts.length; i += 2) {
      const batch = state.missingParts.slice(i, i + 2);
      const { grants } = await post<{ grants: { partNumber: number; url: string }[] }>(
        `${sessionPath}/part-grants`,
        { partNumbers: batch },
      );
      await Promise.all(
        grants.map(async (g) => {
          const [start, end] = partRange(file.size, g.partNumber);
          await putPart(g.url, file.slice(start, end));
          confirmed.add(g.partNumber);
          onProgress({ phase: "uploading", fraction: confirmed.size / state.partCount });
        }),
      );
    }
    state = await get<UploadSessionState>(sessionPath);
    terminal(state);
  }
  if (state.nextAction === "COMPLETE") {
    onProgress({ phase: "completing", fraction: 1 });
    await post(
      `${sessionPath}/complete`,
      {
        sourceManifestHash: await revisionManifestHash(sizeBytes, sha),
        sourceFingerprint: checkpoint.fingerprint,
      },
      checkpoint.completeKey,
    );
  }
  onProgress({ phase: "verifying", fraction: 0 });
  let verified = state.state === "VERIFIED";
  for (let i = 0; !verified && i < 600; i++) {
    const s = await get<UploadSessionState>(sessionPath);
    terminal(s);
    verified = s.state === "VERIFIED";
    if (!verified) await sleep(2000);
  }
  if (!verified)
    throw new Error("Verification is still pending. Reselect the same file later to resume.");

  onProgress({ phase: "publishing", fraction: 1 });
  const revision = await post<{ versionNumber: number }>(
    `${base}/deliverables/${deliverableId}/revisions`,
    { draftId: checkpoint.draftId, notes: checkpoint.notes || undefined },
    checkpoint.publishKey,
  );
  localStorage.removeItem(key);
  onProgress({ phase: "done", fraction: 1 });
  return revision;
}
